import clsx from "clsx";
import React, { useEffect } from "react";
import { RelatedProductsArea } from "web/react/components/__LEGACY__/LEGACY_related-products-area/related-products-area";
import CuratedAds from "web/react/components/__LEGACY__/curated-ads/curated-ads";
import { ProductArea } from "web/react/components/__LEGACY__/product-area/product-area";
import AdsenseShopping from "web/react/components/adsense-shopping/adsense-shopping";
import { AppFooter } from "web/react/components/app-footer/app-footer";
import { BrandAdContainer } from "web/react/components/brand-ad-container";
import { Breadcrumbs } from "web/react/components/breadcrumbs/breadcrumbs";
import Conditional from "web/react/components/conditional/conditional";
import { CustomerCaptureOverlay } from "web/react/components/customer-capture-overlay/customer-capture-overlay";
import { FooterProps } from "web/react/components/footer/footer";
import PDPGallery from "web/react/components/pdp-gallery/pdp-gallery";
import { RecentlyViewed } from "web/react/components/recently-viewed/recently-viewed";
import { RelatedDesignersRail } from "web/react/components/related-designers/related-designers-rail";
import { RelatedProductsSidebar } from "web/react/components/related-products-sidebar/related-products-sidebar";
import { RelatedSearchesRail } from "web/react/components/related-searches-rail/related-searches-rail";
import { VStack } from "web/react/emo/v-stack";
import { useProductSelector } from "web/react/hooks/use-product-selector/use-product-selector";
import { useSFLPrefetch } from "web/react/hooks/use-save-for-later/use-save-for-later";
import layout from "web/react/pages/product/in-stock/LEGACY/in-stock-layout.module.css";
import { gettext } from "web/script/modules/django-i18n";
import userProfiler from "web/script/modules/userprofiler";
import {
    InStockAffiliateProductLayoutSerializer,
    ProductAreaSerializer,
    ProductSerializer,
} from "web/types/serializers";

/**
 * Retrieves placement content for ads.
 *
 * This function returns JSX elements representing ad placements for a given page.
 * On a normal page, it includes two slots at the top and one slot at the bottom.
 * For an express checkout page, it includes zero slots at the top and three slots at the bottom.
 * The function follows a hierarchy for ad placement.
 *
 **/
function getPlacementContent(
    brandAd: InStockAffiliateProductLayoutSerializer["brand_ad"] | undefined,
    curatedAds: InStockAffiliateProductLayoutSerializer["curated_ads"] | undefined,
    adsenseForShopping: InStockAffiliateProductLayoutSerializer["adsense_for_shopping"] | undefined,
    isExpressCheckout: boolean
): { abovePlacement: JSX.Element | null; belowPlacement: JSX.Element | null } {
    const isAdsenseAtTop = !(curatedAds && brandAd);

    const abovePlacement = !isExpressCheckout ? (
        <VStack spacing="lg" dataTestId="ad-products-above">
            {brandAd && (
                <div>
                    <BrandAdContainer {...brandAd} />
                </div>
            )}
            {curatedAds && (
                <div>
                    <CuratedAds {...curatedAds} />
                </div>
            )}
            {adsenseForShopping && isAdsenseAtTop && <AdsenseShopping {...adsenseForShopping} />}
        </VStack>
    ) : null;

    const belowPlacement = (
        <VStack spacing="lg" dataTestId="ad-products-below">
            {isExpressCheckout && (
                <>
                    {brandAd && (
                        <div>
                            <BrandAdContainer {...brandAd} />
                        </div>
                    )}
                    {curatedAds && (
                        <div>
                            <CuratedAds {...curatedAds} />
                        </div>
                    )}
                </>
            )}
            {adsenseForShopping && !isAdsenseAtTop && <AdsenseShopping {...adsenseForShopping} />}
        </VStack>
    );

    return {
        abovePlacement,
        belowPlacement,
    };
}
interface InStockAffiliateProductPageProps extends FooterProps {
    productArea: ProductAreaSerializer;
    productLayout: InStockAffiliateProductLayoutSerializer;
}

function InStockAffiliateProductPage({
    productArea,
    productLayout,
    helpCentreURLs,
    country,
}: InStockAffiliateProductPageProps): React.ReactElement {
    const {
        product_analytics_data: productAnalyticsData,
        product_edit_button: productEditButton,
        product_breadcrumbs: productBreadcrumbs,
        related_products_sidebar: relatedProductsSidebar,
        related_products: relatedProducts,
        related_designers_category_rail: relatedDesignersCategoryRail,
        recently_viewed_products: recentlyViewedProducts,
        curated_ads: curatedAds,
        footer,
        related_search_rail: relatedSearchRail,
        brand_ad: brandAd,
        adsense_for_shopping: adsenseForShopping,
        product_size_picker: productSizesData,
        retailer_slug: retailerSlug,
    } = productLayout;

    useSFLPrefetch(productArea.sorted_variants as string[]);

    const galleryRelatedProducts =
        relatedProducts && relatedProducts.sections.length > 0
            ? relatedProducts.sections[0].related_products.slice(0, 4)
            : [];

    const [currentProduct, setCurrentProduct] = useProductSelector(
        productArea.sorted_variants as string[]
    );

    const product: ProductSerializer = productArea.products[currentProduct];

    useEffect(() => {
        userProfiler.saveSeenProduct(productAnalyticsData);
    }, [productAnalyticsData]);

    const { abovePlacement, belowPlacement } = getPlacementContent(
        brandAd,
        curatedAds,
        adsenseForShopping,
        productArea.show_express_checkout
    );

    return (
        <div className={layout.container}>
            <CustomerCaptureOverlay />

            <div className={layout.primary}>
                <article
                    className={clsx(layout.info, {
                        [layout.infoWithSidebar]: !!relatedProductsSidebar,
                    })}
                >
                    <div className={layout.gallery}>
                        <Conditional
                            check={productEditButton != null && productEditButton.is_user_staff}
                            key="edit-button"
                        >
                            <a
                                href={`https://os.lystit.com/product-data/product/${productEditButton.product_slug}/${productEditButton.product_id}/edit`}
                                target="_blank"
                                rel="noopener noreferrer"
                                className={layout.edit}
                            >
                                <button className="button black">
                                    {gettext("product_edit_button.label")}
                                </button>
                            </a>
                        </Conditional>

                        <PDPGallery
                            key={product.product_id}
                            product={product}
                            relatedProducts={galleryRelatedProducts}
                            hasRelatedProductsSidebar={!!relatedProductsSidebar}
                        />
                    </div>

                    <div className={layout.details}>
                        <ProductArea
                            productArea={productArea}
                            productSizesData={productSizesData}
                            handleProductChange={(id) => setCurrentProduct(id)}
                            selectedVariant={currentProduct}
                            openExpandableArea={false}
                        />
                    </div>
                </article>

                <RelatedProductsSidebar
                    relatedProductsSidebar={relatedProductsSidebar}
                    variant="vertical"
                    className={clsx(layout.sidebar, "affiliateSidebar")}
                />
            </div>
            {(brandAd || adsenseForShopping || relatedProducts || curatedAds) && (
                <div className={layout.secondary}>
                    {/* Show similar products above all promotional ads components only for PLA-EC pdp */}
                    {abovePlacement}
                    {relatedProducts && (
                        <RelatedProductsArea
                            sections={relatedProducts.sections}
                            noBrandAd={!brandAd}
                            noAdsense={!adsenseForShopping}
                            isAffiliate={true}
                            retailerSlug={retailerSlug}
                            onOOS={false}
                        />
                    )}
                    {belowPlacement}
                </div>
            )}

            {relatedDesignersCategoryRail && !!relatedDesignersCategoryRail.designers.length && (
                <RelatedDesignersRail
                    title={relatedDesignersCategoryRail.title}
                    designers={relatedDesignersCategoryRail.designers}
                    showImages={false}
                    eventCategory="pdp_designer_feeds_category"
                />
            )}

            {relatedSearchRail && !!relatedSearchRail.related_searches.length && (
                <div className={clsx(layout.secondary, layout.marginLeft)}>
                    <RelatedSearchesRail
                        title={gettext("product.related_products.related_searches")}
                        relatedSearches={relatedSearchRail?.related_searches}
                    />
                </div>
            )}

            {productBreadcrumbs && (
                <div className={clsx(layout.secondary, layout.marginLeft)}>
                    <Breadcrumbs breadcrumbs={productBreadcrumbs.breadcrumbs} type="pdp" />
                </div>
            )}

            <div className={layout.secondary}>
                <RecentlyViewed
                    title={recentlyViewedProducts.title}
                    products={recentlyViewedProducts.products}
                    is_loading={recentlyViewedProducts.is_loading}
                    current_product_id={recentlyViewedProducts.current_product_id}
                    in_overlay={recentlyViewedProducts.in_overlay}
                    isRedesign={false}
                />
            </div>
            <AppFooter data={footer} helpCentreURLs={helpCentreURLs} country={country} />
        </div>
    );
}

export default InStockAffiliateProductPage;
